import { useState } from 'react';
import { Box, Container, IconButton, Link, Typography, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { matchPath, useLocation } from 'react-router';
import { paths } from '../routes/paths';

const dismissalKey = 'attendin.mobile-app-banner.dismissed-until';
const dismissalDuration = 30 * 24 * 60 * 60 * 1000;
const attendeeRoutes = [
    paths.public.root,
    paths.public.searchEvents,
    paths.public.search,
    paths.public.events.details,
    paths.public.events.confirmation,
    paths.public.myEvents,
    paths.public.communityFeed,
    paths.public.communityDetails,
    paths.public.network,
    paths.public.networkSection,
    paths.public.pods,
    paths.public.podDetails,
];

function getStore() {
    if (typeof navigator === 'undefined') return null;

    if (/iPad|iPhone|iPod/i.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
        return {
            name: 'App Store',
            url: 'https://apps.apple.com/us/app/attendin-events/id6752233973',
        };
    }

    if (/Android/i.test(navigator.userAgent)) {
        return {
            name: 'Google Play',
            url: 'https://play.google.com/store/apps/details?id=com.attendin.attendin',
        };
    }

    return null;
}

function isDismissed() {
    try {
        const expiresAt = Number(window.localStorage.getItem(dismissalKey));
        return Number.isFinite(expiresAt) && expiresAt > Date.now();
    } catch {
        return false;
    }
}

export default function MobileAppBanner() {
    const { pathname } = useLocation();
    const [store] = useState(getStore);
    const [dismissed, setDismissed] = useState(isDismissed);
    const isStandalone = useMediaQuery('(display-mode: standalone)');
    const isIosStandalone = typeof navigator !== 'undefined'
        && (navigator as Navigator & { standalone?: boolean }).standalone === true;

    if (!store || dismissed || isStandalone || isIosStandalone
        || !attendeeRoutes.some(route => matchPath({ path: route, end: true }, pathname))) {
        return null;
    }

    const dismiss = () => {
        setDismissed(true);
        try {
            window.localStorage.setItem(dismissalKey, String(Date.now() + dismissalDuration));
        } catch {
            return;
        }
    };

    return (
        <Box component="aside" aria-label="AttendIn mobile app" sx={{ bgcolor: 'background.paper', flexShrink: 0 }}>
            <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: { xs: 2, sm: 3 } }}>
                <Box component="img" src="/logo-only.png" alt="" sx={{ width: theme => theme.spacing(4), height: theme => theme.spacing(4), objectFit: 'contain', flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" component="p">AttendIn</Typography>
                    <Typography variant="caption" component="p" color="text.secondary">Your event app</Typography>
                </Box>
                <Link
                    href={store.url}
                    aria-label={`Get AttendIn on the ${store.name}`}
                    variant="body2"
                    underline="hover"
                    sx={{ display: 'inline-flex', alignItems: 'center', minHeight: theme => theme.spacing(5.5), flexShrink: 0, px: 0.5 }}
                >
                    Get the app
                </Link>
                <IconButton
                    aria-label="Dismiss app banner"
                    onClick={dismiss}
                    sx={{ width: theme => theme.spacing(5.5), height: theme => theme.spacing(5.5), flexShrink: 0, color: 'text.secondary' }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Container>
        </Box>
    );
}